import { BottomNavigation, BottomNavigationAction } from "@mui/material";
import {
  ChatsCircleIcon,
  MagnifyingGlassIcon,
  SuitcaseSimpleIcon,
  UsersIcon,
} from "@phosphor-icons/react";
import { useTranslations } from "use-intl";
import { useMatchRoute, useNavigate } from "@tanstack/react-router";
import { rudderAnalytics, CommonTrackingEvents } from "../lib/rudderstack";

const ROUTES = {
  search: "/",
  bookings: "/bookings",
  travelers: "/travelers",
  chat: "/chat",
} as const;

const eventMap = {
  search: CommonTrackingEvents.USER_PRESSED_SEARCH_NAVIGATION_BUTTON,
  travelers: CommonTrackingEvents.USER_PRESSED_TRAVELERS_NAVIGATION_BUTTON,
  bookings: CommonTrackingEvents.USER_PRESSED_MY_TRIPS_NAVIGATION_BUTTON,
  chat: CommonTrackingEvents.USER_PRESSED_CHAT_NAVIGATION_BUTTON,
} as const;

const Footer = () => {
  const t = useTranslations();
  const navigate = useNavigate();
  const matches = useMatchRoute();

  const handleChange = (
    _: React.SyntheticEvent,
    newValue: keyof typeof ROUTES,
  ) => {
    rudderAnalytics.track(eventMap[newValue]);

    navigate({
      to: ROUTES[newValue],
    });
  };

  const getCurrentTabValue = (): keyof typeof ROUTES => {
    const routes = [
      { route: ROUTES.bookings, value: "bookings" },
      { route: ROUTES.travelers, value: "travelers" },
      { route: ROUTES.chat, value: "chat" },
    ] as const;

    for (const { route, value } of routes) {
      if (matches({ to: route, fuzzy: true })) {
        return value;
      }
    }

    return "search"; // if no other values match, we're on 'search' page
  };

  return (
    <BottomNavigation
      dir="ltr" // we want to keep consistent order here
      className="fixed bottom-0 z-20 w-full max-w-xl flex-row rounded-t-2xl border-t border-t-slate-300 pt-2 pb-2"
      showLabels
      value={getCurrentTabValue()}
      onChange={handleChange}
      sx={{
        height: "calc(env(safe-area-inset-bottom) + var(--footer-height))",
        paddingBottom: "calc(env(safe-area-inset-bottom) + 0.5rem)",
      }}
    >
      <BottomNavigationAction
        label={t("search")}
        icon={<MagnifyingGlassIcon size={24} color="#003E2B" aria-hidden />}
        value="search"
        disableRipple
      />
      <BottomNavigationAction
        label={t("myBookings")}
        icon={<SuitcaseSimpleIcon size={24} color="#003E2B" aria-hidden />}
        value="bookings"
        disableRipple
      />
      <BottomNavigationAction
        label={t("travellers.title")}
        icon={<UsersIcon size={24} color="#003E2B" aria-hidden />}
        value="travelers"
        disableRipple
      />
      <BottomNavigationAction
        label={t("chat.title")}
        icon={<ChatsCircleIcon size={24} color="#003E2B" aria-hidden />}
        value="chat"
        disableRipple
      />
    </BottomNavigation>
  );
};

export default Footer;
